import {
  ButtonDS,
  DialogModalDS,
  Spacer,
  TableDS,
  MessageTooltip,
  LoaderOverlay,
  useTableVehicleAnalyticDetailsRow,
  TableVehicleAnalyticDetailsRow,
} from "@qivia/ui";
import { selectCompany } from "../../homeSlice";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useCallback, useEffect } from "react";
import {
  requestVehiclesConnectionAsync,
  selectListAllVehiclesInDb,
  selectRequestVehiclesConnectionStatus,
} from "../vehiclesSlice";
import {
  DATA_PROVIDER_BY_CONNECTION_TYPE,
  findVehicleConnectionType,
} from "../vehicleConnection";
import styled from "styled-components";

export const AddSingleVehicleRequestConnectionModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
  vehicleUuid: string;
}) => {
  const { t } = useTranslation();
  const { render, headers } = useTableVehicleAnalyticDetailsRow();
  const { onCloseModal, visible, vehicleUuid } = props;
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const vehiclesListInDb = useAppSelector(selectListAllVehiclesInDb);
  const requestVehiclesConnectionStatus = useAppSelector(
    selectRequestVehiclesConnectionStatus,
  );

  const [loader, setLoader] = useState<boolean>(false);

  const vehicle = useMemo(
    () => vehiclesListInDb.find((v) => v.uuid === vehicleUuid) ?? null,
    [vehiclesListInDb, vehicleUuid],
  );

  const handleCloseModal = useCallback(() => {
    setLoader(false);
    onCloseModal();
  }, [onCloseModal]);

  const isValidForm = useMemo(() => company !== null, [company]);

  const handleSubmitVehicleRequestConnection = useCallback(() => {
    if (!company || !vehicle) return;

    void dispatch(
      requestVehiclesConnectionAsync({
        companyUuid: company.uuid,
        companyName: company.name,
        connectionPlan: "LIGHT",
        vehicles: [
          {
            registrationNumber: vehicle.registrationNumber,
            uuid: vehicle.uuid,
            dataProvider:
              DATA_PROVIDER_BY_CONNECTION_TYPE[findVehicleConnectionType(vehicle)],
          },
        ],
      }),
    );

    setLoader(true);
  }, [company, vehicle, dispatch]);

  useEffect(() => {
    if (requestVehiclesConnectionStatus === "success") {
      handleCloseModal();
    }
  }, [requestVehiclesConnectionStatus, handleCloseModal]);

  if (!vehicle) return;

  const vehicleRow: TableVehicleAnalyticDetailsRow = {
    reference: vehicle.reference,
    registrationNumber: vehicle.registrationNumber,
    connectionType: findVehicleConnectionType(vehicle),
  };

  return (
    <DialogModalDS
      visible={visible}
      width={44}
      onClose={handleCloseModal}
      deactivateClickOutsideSidePanel={loader}
      title={t("vehicles.modal.requestConnection.title") || ""}
      subTitle={t("vehicles.modal.requestConnection.single.subTitle") || ""}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("vehicles.modal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={handleCloseModal}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("vehicles.modal.requestConnection")}
            format={"fill"}
            buttonType={"primary"}
            onClick={handleSubmitVehicleRequestConnection}
            disabled={!isValidForm || loader}
          />
        ),
      }}
    >
      <StyledRequestModalContainer>
        <LoaderOverlay visible={loader} />
        <TableDS<
          keyof TableVehicleAnalyticDetailsRow,
          TableVehicleAnalyticDetailsRow
        >
          data={[vehicleRow]}
          headers={headers}
          render={render}
        />
        {!vehicle.isEligible && (
          <>
            <Spacer y={1} />
            <MessageTooltip
              title={t("vehicles.modal.requestConnection.tooltip.title")}
              text={t("vehicles.modal.requestConnection.tooltip.message")}
              type="INFORMATIVE"
            />
          </>
        )}
      </StyledRequestModalContainer>
    </DialogModalDS>
  );
};

const StyledRequestModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;
